/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import Button from '@material-ui/core/Button';
import Grid, {GridSize} from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import {errorOptions, successOptions} from '@ion/applications/src/prompts/constants';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Asset,
  CRUDPermissions,
  Device,
  Entity,
  GenericObject
} from '@ion/core/src/types/entities';
import {booleanToString} from '@ion/core/src/utils/BooleanUtils';
import {getCurrentDateTime} from '@ion/core/src/utils/DateUtil';
import {
  getDeviceIdentifierFromType,
  getScriptFileName,
  getVersionByPlatformId,
  isLMUDevice as isValidLMUDevice,
  isMDTDevice as isValidMDTDevice,
  PLATFORM_IDS
} from '@ion/core/src/utils/DeviceUtils';
import {itemsToCSV} from '@ion/core/src/utils/StringUtil';
import Chip, {ChipVariant} from '@ion/dls/src/components/chip/Chip';
import {useIdentifierResults} from '@ion/dls/src/hooks/useIdentifierResults';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {translateStatus} from '@ion/dls/src/utils/ComponentUtils';
import {useCreateJob} from '@ion/query/src/hooks/useCreateJob';
import {useFileHistoryCurrent} from '@ion/query/src/hooks/useFileHistoryCurrent';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import DeviceDialogGroupDetails from './DeviceDialogGroupDetails';
import {useVbusEnabledAccess} from './useVbusEnabledAccess';
import {
  getAVLAddress,
  getDeviceIdentifier,
  getFirstIdReport,
  getMDN,
  getVbusStatus,
  vbusStatusText
} from './ViewDeviceEditorUtils';

export interface DeviceDetailsTabType {
  onSuccess?: () => void;
  selectedTab: Number;
  tabIndex: Number;
  device: Device;
  deviceDetail: Device;
  assetData?: Asset;
  manufacturingInfo?: GenericObject;
  devicesPermissions?: CRUDPermissions;
  Features?: GenericObject;
  scriptDesc?: string;
  isVbuFwEnabled?: boolean;
}

export interface GridItemProps {
  mdSpacing?: number;
  label?: string;
  value?: any;
  classes?: any;
  children?: JSX.Element;
}

interface StylesProps {
  isVBusOff?: boolean;
}

const useStyles = makeStyles(() => ({
  formSection : {
    ...dlsAlt('typography', 'cta3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    letterSpacing : '0',
    padding       : '20px 0 10px 0'
  },
  leftContainer : {
    flex            : 1,
    backgroundColor : dlsColor('white'),
    marginRight     : '20px',
    padding         : '10px 0'
  },
  rightContainer : {
    flex            : 1,
    backgroundColor : dlsColor('white'),
    margin          : '0 10px',
    padding         : '10px 0'
  },
  flexStyle : {
    display : 'flex'
  },
  vbusText : {
    ...dlsAlt('typography', 'label2'),
    color : ({
      isVBusOff
    }: StylesProps) => isVBusOff ? dlsColor('caRed') : dlsColor('grey1'),
    fontSize      : '16px',
    letterSpacing : 0,
    padding       : '0 20px'
  },
  buttonContainer : {
    display         : 'flex',
    justifyContent  : 'space-between',
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    margin          : '5px 15px',
    padding         : '5px',
    minHeight       : '36px'
  },
  button : {
    color    : dlsColor('aaBlue'),
    fontSize : 12,
    height   : 'auto'
  },
  gridContainer : {
    width      : '100%',
    paddingTop : '10px'
  }
}));

const useGridStyles = makeStyles(() => ({
  headerItem : {
    ...dlsAlt('typography', 'label3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    fontSize      : '14px',
    letterSpacing : 0,
    padding       : '5px 20px'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color           : dlsColor('grey1'),
    backgroundColor : dlsColor('white'),
    margin          : '5px 15px',
    fontSize        : '16px',
    minHeight       : '16px',
    letterSpacing   : 0,
    padding         : '15px',
    overflowWrap    : 'break-word',
    wordWrap        : 'break-word'
  },
  multiItemBody : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey1'),
    fontSize      : '16px',
    minHeight     : '1px',
    letterSpacing : 0,
    padding       : '8px 20px 8px'
  },
  tagChipContainer : {
    '& > *' : {
      margin : '5px'
    }
  }
}));

export const GridItem = ({label = '', value = '', mdSpacing = 4}: GridItemProps) => {
  const classes = useGridStyles();
  return (
    <Grid item md={mdSpacing as GridSize} sm={6} xs={12}>
      <div className={classes.headerItem}>
        {label}
      </div>
      <div className={classes.bodyItem}>
        {value}
      </div>
    </Grid>
  );
};

interface AssociatedDevicesGridItemProps extends GridItemProps {
  labels: {
    associatedDevicesTitle?: string;
    tags?: string[];
  }
  tags?: string[];
  associatedTitle?: string;
  associatedLabel?: string;
  associatedValue?: string;
}

/**
 * Displays the alternate 'associated devices' section including the device tags and any
 * associated LMU as chips.
 */
export const AssociatedDevicesGridItem = memo(({
  labels: {
    associatedDevicesTitle,
    tags: tagsLabel
  },
  associatedTitle,
  associatedLabel,
  associatedValue,
  tags
}: AssociatedDevicesGridItemProps) => {
  if (typeof tags === 'string') {
    tags = [tags];
  }
  const classes = useGridStyles();
  return (
    <Grid item md={12}>
      <div className={classes.headerItem}>
        {associatedDevicesTitle}
      </div>
      <div className={classes.multiItemBody}>
        <span>{associatedTitle}</span>
        {associatedValue && (
          <span className={classes.tagChipContainer}>
            <Chip colorVariant={ChipVariant.GREEN}
              borderRadius={'4px'}
              labelColor={dlsColor('grey2')}
              label={`${associatedLabel}: ${associatedValue}`} />
          </span>
        )}
      </div>
      <div className={classes.multiItemBody}>
        <span>{`${tagsLabel}: `}</span>
        <span className={classes.tagChipContainer}>{tags?.map((tag, index) => (
          <Chip key={`device-tag-${index}`}
            borderRadius={'4px'}
            labelColor={dlsColor('grey2')}
            label={tag} />
        ))}</span>
      </div>
    </Grid>
  );
});

/**
 * DeviceDetailsTab component that displays the Device data
 * for the selected Device Details tab.
 */
export const DeviceDetailsTab = ({
  onSuccess,
  devicesPermissions,
  device,
  deviceDetail,
  assetData,
  manufacturingInfo,
  Features,
  tabIndex = 0,
  selectedTab = 0,
  scriptDesc,
  isVbuFwEnabled = false
}: DeviceDetailsTabType) => {
  const [isVBusOff, setIsVBusOff]  = useState<boolean>(false);
  const classes                    = useStyles({isVBusOff});
  const gridClasses                = useGridStyles();
  const dispatch                   = useDispatch();
  const canHaveVbusAccess          = useVbusEnabledAccess(Features);
  const associatedDevices          = useMemo(() => device?.extensions?.associatedDevices?.[0], [device]);
  const isMDTDevice                = isValidMDTDevice({category: associatedDevices?.deviceCategory});
  const isLMUDevice                = isValidLMUDevice({category: associatedDevices?.deviceCategory});
  const vbusStatus                 = getVbusStatus(device);
  const {hasUpdatePermission}      = devicesPermissions || {};
  const deviceIdentifier           = getDeviceIdentifierFromType(device);
  const {currentFileHistory}       = useFileHistoryCurrent(deviceIdentifier);
  const scriptName                 = getScriptFileName(currentFileHistory);
  const deviceViewPath             = 'admin-devices:view.additionalInfo';

  const {
    itemToDisplay,
    setShowDialog,
    itemLength,
    showDialog,
    moreItem
  } = useIdentifierResults({
    data           : device?.groups,
    displayedItems : 1
  }) || {};

  const assoicatedDeviceIdentifier = useMemo(() => {
    const associatedDevicesIsArray = Array.isArray(device?.extensions?.associatedDevices);
    return {
      label : translate('admin-devices:view.generalDeviceInfo.associatedDevices.esn', {returnObjects: true}),
      value : associatedDevicesIsArray ? device?.extensions?.associatedDevices.map(e => e?.mobileId).join(',') : ''
    };
  }, [device]);

  const vbusButtonText = useMemo(() => (
    translate(`admin-devices:view.vbus.${vbusStatus === vbusStatusText.off ? 'turnOn' : 'turnOff'}`)
  ), [vbusStatus]);

  const obdControlName = useMemo(() => {
    const isTurnedOff = device?.extensions?.vbusEnabledStatus;
    return translate(`admin-devices:view.vbus.${isTurnedOff ? 'obdControlOff' : 'obdControlOn'}`);
  }, [device]);

  const deviceTags = useMemo(() => {
    const assets = assetData?.assets;
    if (assets?.length && isMDTDevice) {
      return assets.map(asset => asset?.title);
    }
    return itemsToCSV(assets, 'title') || [];
  }, [isMDTDevice, assetData]);

  const {
    save,
    isSuccess,
    isError,
    isLoading
  } = useCreateJob(device?.id);

  useEffect(() => {
    if (isSuccess || isError) {
      dispatch(toggleUserPrompt({
        ...(isSuccess ? successOptions : errorOptions),
        message : translate(`admin-devices:view.vbus.${isSuccess ? 'success' : 'error'}Message`,
          {entity: translate('common:entityLabels.device')})
      }));
      isSuccess && onSuccess?.();
    }
  }, [isSuccess, isError]);

  useEffect(() => {
    setIsVBusOff(vbusStatus === vbusStatusText.off);
  }, [vbusStatus]);

  const updateVbusStatus = useCallback(() => {
    const {account} = device;
    const identifier = getDeviceIdentifier(device);
    const accountId = entityId(account);
    const status = vbusStatus === vbusStatusText.off ? vbusStatusText.on : vbusStatusText.off;
    const newJob = {
      groups      : null,
      description : '',
      name        : `${identifier} ${translate('admin-devices:view.vbus.entity')} ${getCurrentDateTime()}`,
      account     : account,
      criteria    : [{
        memberSearch : {
          searchTerms : {
            account : accountId,
            esn     : identifier
          },
          type : Entity.device
        }
      }],
      tasks : [{
        task : {
          description : '',
          name        : `${identifier} / ${obdControlName}`,
          taskType    : translate('admin-devices:view.vbus.taskType'),
          metaData    : {
            commandType : translate('admin-devices:view.vbus.commandType'),
            obdControl  : {
              action : status
            }
          },
          account : account
        }
      }
      ]
    };

    save({job: newJob});
  }, [device, vbusStatus]);

  return (
    <div id={`detailsTab_${tabIndex}`} hidden={selectedTab !== tabIndex}>
      <div className={classes.formSection}>{translate('common:form.asset.general')}</div>
      <Grid container
        spacing={0}
        alignItems='flex-start'
        justifyContent='flex-start'
        className={classes.gridContainer}>
        <GridItem label={translate('admin-devices:view.generalDeviceInfo.deviceName')}
          value={deviceDetail?.name || device?.name} />
        <GridItem label={translate('admin-devices:view.generalDeviceInfo.deviceId')}
          value={device?.id} />
        <GridItem label={translate('admin-devices:view.generalDeviceInfo.account')}
          value={deviceDetail?.account?.title || device?.account?.title} />
        <GridItem label={translate('admin-devices:view.generalDeviceInfo.deviceConfiguration')}
          value={deviceDetail?.deviceConfiguration?.title || device?.deviceConfiguration?.title} />
        <GridItem label={translate('admin-devices:view.deviceDetailSummary.assetName')}
          value={device?.asset?.title} />
        <GridItem label={translate('admin-devices:view.deviceDetailSummary.deviceStatus')}
          value={translateStatus(deviceDetail?.status || device?.status)} />
        <GridItem label={translate('admin-devices:view.generalDeviceInfo.firstIDReport')}
          value={getFirstIdReport(device)} />
        <GridItem label={translate('admin-devices:view.generalDeviceInfo.lastReportedAvlAddress')}
          value={getAVLAddress(device)} />
        <GridItem label={translate('admin-devices:view.generalDeviceInfo.groups')}
          value={<DeviceDialogGroupDetails groupName={device?.groups?.[0]?.title}
            groups={device?.groups}
            deviceName={device?.name}
            itemToDisplay={itemToDisplay}
            setShowDialog={setShowDialog}
            itemLength={itemLength}
            showDialog={showDialog}
            moreItem={moreItem} />} />
        <GridItem label={translate(`${deviceViewPath}.iccid`)}
          value={device?.iccid} />
        <GridItem label={translate(`${deviceViewPath}.imsi`)}
          value={device?.imsi} />
        <GridItem label={translate(`${deviceViewPath}.phone`)}
          value={getMDN(device)} />
        <GridItem label={translate(`${deviceViewPath}.partDesc`)}
          mdSpacing={12}
          value={manufacturingInfo?.Description || manufacturingInfo?.description} />
        {(isMDTDevice || isLMUDevice) && assoicatedDeviceIdentifier ? (
          <>
            <GridItem label={translate('admin-devices:view.generalDeviceInfo.scriptName')}
              mdSpacing={12}
              value={scriptName || ''} />
            <GridItem label={translate('admin-devices:view.generalDeviceInfo.scriptDescription')}
              mdSpacing={12}
              value={scriptDesc || ''} />
            <AssociatedDevicesGridItem labels={translate('admin-devices:view.generalDeviceInfo.associatedDevices', {returnObjects: true})}
              associatedTitle={translate('admin-devices:view.generalDeviceInfo.associatedDevices.associatedDeviceLabel', {
                category      : translate(`admin-devices:view.generalDeviceInfo.associatedDevices.${isLMUDevice ? 'lmu' : 'mdt'}`, {returnObjects: true}),
                returnObjects : true
              })}
              associatedLabel={assoicatedDeviceIdentifier.label}
              associatedValue={assoicatedDeviceIdentifier.value}
              tags={deviceTags as string[]} />
          </>
        ) : (
          <GridItem label={translate('admin-devices:view.generalDeviceInfo.deviceTag')}
            value={deviceTags} />
        )}
        <GridItem label={translate('admin-devices:view.generalDeviceInfo.buildNumber')}
          value={device?.idReport?.device?.idReport?.buildNumber || manufacturingInfo?.buildNumber} />
        {
          !((isMDTDevice || isLMUDevice) && assoicatedDeviceIdentifier) && (
            <>
              <GridItem label={translate('admin-devices:view.generalDeviceInfo.scriptName')}
                mdSpacing={12}
                value={scriptName || ''} />
              <GridItem label={translate('admin-devices:view.generalDeviceInfo.scriptDescription')}
                mdSpacing={12}
                value={scriptDesc || ''} />
            </>
          )
        }
      </Grid>

      <div className={classes.formSection}>{translate('admin-devices:view.status.statusTitle')}</div>
      <Grid container
        spacing={0}
        alignItems='flex-start'
        justifyContent='flex-start'
        className={classes.gridContainer}>
        <Grid item md={4} sm={6} xs={12}>
          <div className={gridClasses.headerItem}>
            {translate('admin-devices:view.status.vbusEnableStatus')}
          </div>
          <div className={classes.buttonContainer}>
            <div className={classes.vbusText}>
              {vbusStatus}
            </div>
            {canHaveVbusAccess && vbusStatus && (
              <Button className={classes.button}
                variant='outlined'
                disabled={isLoading || !hasUpdatePermission}
                onClick={updateVbusStatus}>
                {vbusButtonText}
              </Button>
            )}
          </div>
        </Grid>
        <GridItem label={translate('admin-devices:view.status.vbusMode')}
          value={device?.extensions?.vbusMode} />
        <GridItem label={translate('admin-devices:view.status.configParamReadError')}
          value={booleanToString(device?.idReport?.isUnitStatus1)} />
        <GridItem label={translate('admin-devices:view.status.isGPSAntennaStatusOK')}
          value={booleanToString(device?.idReport?.isGPSAntennaStatusOK)} />
        <GridItem label={translate('admin-devices:view.status.isGPSTrackingOK')}
          value={booleanToString(device?.idReport?.isGPSTrackingOK)} />
        <GridItem label={translate('admin-devices:view.status.isGPSReceiverTestOK')}
          value={booleanToString(device?.idReport?.isGPSReceiverTestOK)} />
      </Grid>

      <div className={classes.formSection}>{translate(`${deviceViewPath}.title`)}</div>
      <Grid container
        spacing={0}
        alignItems='flex-start'
        justifyContent='flex-start'
        className={classes.gridContainer}>
        <GridItem label={translate(`${deviceViewPath}.esn`)}
          value={deviceDetail?.esn || device?.esn} />
        <GridItem label={translate(`${deviceViewPath}.serialNumber`)}
          value={deviceDetail?.serialNumber || device?.serialNumber} />
        <GridItem label={translate(`${deviceViewPath}.airId`)}
          value={deviceDetail?.airId || device?.airId} />
        <GridItem label={translate(`${deviceViewPath}.imei`)}
          value={device?.imei} />
        <GridItem label={translate(`${deviceViewPath}.imei2`)}
          value={device?.imei2} />
        <GridItem label={translate(`${deviceViewPath}.isdn`)}
          value={device?.isdn} />
        <GridItem label={translate(`${deviceViewPath}.meid`)}
          value={device?.meid} />
        <GridItem label={translate(`${deviceViewPath}.partInfo`)}
          value={manufacturingInfo?.UNITPARTNUMBER || manufacturingInfo?.unitpartNumber ||
            manufacturingInfo?.partNumber} />
        <GridItem label={translate(`${deviceViewPath}.carrierStatus`)}
          value={device?.carrierStatus} />
        <GridItem label={translate(`${deviceViewPath}.vehicleClass`)}
          value={device?.idReport?.device?.idReport?.vehicleClass} />
        <GridItem label={translate(`${deviceViewPath}.ble3Firmware`)}
          value={getVersionByPlatformId(device, PLATFORM_IDS.ble3Firmware)} />
        <Render.If condition={!isVbuFwEnabled}>
          <GridItem label={translate(`${deviceViewPath}.vbu3Firmware`)}
            value={getVersionByPlatformId(device, PLATFORM_IDS.vbu3Firmware)} />
        </Render.If>
        <GridItem label={translate(`${deviceViewPath}.bspModemFirmware`)}
          value={getVersionByPlatformId(device, PLATFORM_IDS.bspModemFirmware)} />
        <GridItem label={translate(`${deviceViewPath}.cellRadioFirmware`)}
          value={getVersionByPlatformId(device, PLATFORM_IDS.cellRadioFirmware)} />
        <GridItem label={translate(`${deviceViewPath}.note`)}
          mdSpacing={12}
          value={deviceDetail?.deviceNote} />
      </Grid>
    </div>
  );
};

export default DeviceDetailsTab;
